<script setup lang="ts">
import { ActionButton, Mark } from '@taler/ui'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/app/auth/use-auth'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuth()
const starting = ref(false)
const devSigningIn = ref(false)

const declined = route.query['declined'] === '1'
const returnTo = () => (typeof route.query['returnTo'] === 'string' ? route.query['returnTo'] : '/')

// import.meta.env.DEV folds to `false` in a production build, eliminating this whole path.
const devEmail = import.meta.env.DEV ? import.meta.env['VITE_TALER_DEV_SIGN_IN_EMAIL'] : undefined

async function start(): Promise<void> {
  starting.value = true
  await auth.signIn.begin(returnTo())
}

async function startDev(): Promise<void> {
  if (devEmail === undefined) return
  devSigningIn.value = true
  try {
    await auth.completeDevSignIn?.(devEmail)
    await router.push(returnTo())
  } finally {
    devSigningIn.value = false
  }
}
</script>

<template>
  <main class="mx-auto flex max-w-xl flex-col gap-4 p-8">
    <Mark :size="48" :strike="true" />
    <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
      {{ t('auth.signInTitle') }}
    </h1>
    <p class="text-[color:var(--color-text-muted)]">{{ t('auth.signInBody') }}</p>
    <p v-if="declined" role="alert" class="text-[color:var(--color-danger)]">
      {{ t('auth.signInDeclined') }}
    </p>
    <div>
      <ActionButton type="button" :disabled="starting" class="font-medium" @click="start">
        {{ t('auth.signInAction') }}
      </ActionButton>
    </div>
    <div v-if="devEmail !== undefined">
      <button
        type="button"
        :disabled="devSigningIn"
        class="min-h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] px-4 py-2 font-medium text-[color:var(--color-text)]"
        @click="startDev"
      >
        {{ t('auth.devSignInAction', { email: devEmail }) }}
      </button>
    </div>
  </main>
</template>
